import { Pill, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { EcosystemClassificationType } from '@/api/rest/resources/types/ecosystemClassification';
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { NCCardSectionHeader, printLatestDate } from '@/components/NCCard/NCCard';
import { getClassNameValuesFromFacts } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/ecosystemClassification/utils';

type EcosystemClassificationStatsTileProps = {
  firstScalarFacts: PlotReportFactElement<number | null>[] | undefined;
  secondScalarFacts: PlotReportFactElement<number | null>[] | undefined;
  category: EcosystemClassificationType;
};

export const EcosystemClassificationStatsTile = ({
  firstScalarFacts,
  secondScalarFacts,
  category,
}: EcosystemClassificationStatsTileProps) => {
  const { t } = useTranslation();

  const firstScalarValues = getClassNameValuesFromFacts({ facts: firstScalarFacts });
  const secondScalarValues = getClassNameValuesFromFacts({ facts: secondScalarFacts });

  const text = {
    firstScalar: {
      label: t(`shared.ncaDetail.ecosystemClassification.${category}.labels.firstScalarFact`),
      tooltip: t(`shared.ncaDetail.ecosystemClassification.${category}.labels.firstScalarFactTooltip`),
    },
    secondScalar: {
      label: t(`shared.ncaDetail.ecosystemClassification.${category}.labels.secondScalarFact`),
      tooltip: t(`shared.ncaDetail.ecosystemClassification.${category}.labels.secondScalarFactTooltip`),
    },
    whatDoesThisMean: {
      title: t(`shared.ncaDetail.ecosystemClassification.${category}.explainers.whatDoesThisMean.title`),
      body: t(`shared.ncaDetail.ecosystemClassification.${category}.explainers.whatDoesThisMean.body`),
    },
  };

  const latestAnalysisDate = printLatestDate([firstScalarFacts?.[0]?.measured_at]);

  return (
    <Stack className='gap-8 px-6 py-8 lg:gap-4' data-testid='ecosystem-classification-stats-tile'>
      <Stack spacing={4}>
        {firstScalarValues?.length > 0 && (
          <>
            <NCCardSectionHeader
              title={text.firstScalar.label}
              popover={{ title: text.firstScalar.label, body: text.firstScalar.tooltip }}
              supplementaryInfo={`${t('shared.ncaDetail.details.labels.latestAnalysis')} ${latestAnalysisDate}`}
            />
            <Stack direction='row' spacing={2} className='flex-wrap'>
              {firstScalarValues.map((value) => (
                <Pill key={value} size='small' className='bg-info-light text-info'>
                  {value}
                </Pill>
              ))}
            </Stack>
          </>
        )}
        {secondScalarValues?.length > 0 && (
          <>
            <NCCardSectionHeader
              title={text.secondScalar.label}
              popover={{ title: text.secondScalar.label, body: text.secondScalar.tooltip }}
            />
            <Stack direction='row' spacing={2} className='flex-wrap'>
              {secondScalarValues.map((value) => (
                <Pill key={value} size='small' className='bg-success-light text-success-dark'>
                  {value}
                </Pill>
              ))}
            </Stack>
          </>
        )}
        <Stack spacing={4} className='mt-2 rounded-2xl bg-neutral-black-4 px-4 py-6'>
          <span className='typography-body1Bold'>{text.whatDoesThisMean.title}</span>
          <p className='typography-body1 text-text-secondary'>{text.whatDoesThisMean.body}</p>
        </Stack>
      </Stack>
    </Stack>
  );
};
